import { MantineColor, useMantineTheme } from '@mantine/core';
import { ReactNode, isValidElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { CaretDown, CaretUp, Equal } from 'tabler-icons-react';

export function IncreaseIndicator({ value, size, preferDecrease }: { value: number; size: 'sm' | 'md' | 'lg'; preferDecrease?: boolean }) {
    const theme = useMantineTheme();
    const sizePx = size === 'sm' ? 14 : size === 'md' ? 16 : 20;
    const warn = preferDecrease ? value > 0 : value < 0;
    const colorName: MantineColor = value === 0 ? 'gray' : warn ? 'warning' : 'success';
    const color = theme.colors[colorName][theme.colorScheme === 'dark' ? 4 : 6];
    const Icon = value === 0 ? Equal : value < 0 ? CaretDown : CaretUp;
    return <Icon size={sizePx} fill={color} color={color} />;
}

export function useNumberInputFractions(
    value?: number,
    explicitFractions?: number,
    mask?: { parser?: (value?: string) => string; formatter?: (value?: string) => string }
) {
    const getPrecision = useCallback((val: number) => {
        const parts = val.toString().split('.');
        return parts.length > 1 ? parts[1].length : 0;
    }, []);
    const [fractions, setFractions] = useState(explicitFractions ?? getPrecision(value ?? 0));
    const [parser, formatter] = useMemo(() => {
        let parser = mask?.parser;
        let formatter = mask?.formatter;
        if (explicitFractions === undefined) {
            const baseParser = parser ?? ((v?: string) => v?.replace(/[^0-9\-\.]*/g, '') ?? '');
            const baseFormatter = formatter ?? ((v?: string) => v?.toString() ?? '');
            parser = (v?: string) => {
                const val = baseParser(v);
                if (val) {
                    setFractions(getPrecision(parseFloat(val)));
                }
                return val;
            };
            formatter = baseFormatter;
        }
        return [parser, formatter];
    }, [mask?.parser, mask?.formatter, explicitFractions]);

    return { precision: fractions, parser, formatter };
}
